// src/Pages/ReceptionistDashboard/PsychiatricReportsAnalytics.js

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  TextField,
  Divider,
  useTheme,
} from '@mui/material';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Chart,
  LineElement,
  BarElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';

// Register necessary chart components for Chart.js
Chart.register(
  LineElement,
  BarElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const PsychiatricReportsAnalytics = () => {
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [reportType, setReportType] = useState('daily');
  const theme = useTheme();

  // Mock data for charts
  const sessionTypeData = {
    labels: [
      'Individual Therapy',
      'Group Therapy',
      'Medication Management',
      'Emergency Consultation',
    ],
    datasets: [
      {
        label: 'Sessions Conducted',
        data: [40, 15, 20, 5],
        backgroundColor: ['#36A2EB', '#FF6384', '#FFCE56', '#4BC0C0'],
      },
    ],
  };

  const revenueData = {
    labels: [
      'Therapy Sessions',
      'Psychiatric Evaluations',
      'Medication Management',
    ],
    datasets: [
      {
        label: 'Revenue',
        data: [5000, 3000, 2000],
        backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56'],
      },
    ],
  };

  const demographicsData = {
    labels: ['Male', 'Female', 'Non-binary'],
    datasets: [
      {
        data: [50, 45, 5],
        backgroundColor: ['#36A2EB', '#FF6384', '#FFCE56'],
      },
    ],
  };

  const commonDiagnosesData = {
    labels: [
      'Anxiety',
      'Depression',
      'PTSD',
      'Bipolar Disorder',
      'Schizophrenia',
    ],
    datasets: [
      {
        label: 'Number of Patients',
        data: [30, 25, 10, 5, 5],
        backgroundColor: [
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#4BC0C0',
          '#9966FF',
        ],
      },
    ],
  };

  const handleReportTypeChange = (event) => {
    setReportType(event.target.value);
  };

  const handleExportReport = () => {
    // Logic to export reports as PDF or CSV
    console.log('Exporting report...');
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant='h4' gutterBottom>
        Psychiatric Clinic Reports and Analytics
      </Typography>

      {/* Date Range Selector */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Grid container spacing={2} sx={{ marginBottom: 2 }}>
          <Grid item xs={12} md={4}>
            <DatePicker
              label='Start Date'
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DatePicker
              label='End Date'
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Button
              variant='contained'
              startIcon={<DownloadIcon />}
              onClick={handleExportReport}
              fullWidth
            >
              Export Report
            </Button>
          </Grid>
        </Grid>
      </LocalizationProvider>

      {/* Report Type Selector */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Report Type</InputLabel>
            <Select
              value={reportType}
              onChange={handleReportTypeChange}
              label='Report Type'
            >
              <MenuItem value='daily'>Daily</MenuItem>
              <MenuItem value='monthly'>Monthly</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Reports Section */}
      <Grid container spacing={3}>
        {/* Session Types */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ padding: 3 }}>
            <Typography variant='h6' gutterBottom>
              Session Types Breakdown
            </Typography>
            <Bar data={sessionTypeData} />
          </Paper>
        </Grid>

        {/* Revenue Breakdown */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ padding: 3 }}>
            <Typography variant='h6' gutterBottom>
              Revenue Breakdown
            </Typography>
            <Pie data={revenueData} />
          </Paper>
        </Grid>

        {/* Patient Demographics */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ padding: 3 }}>
            <Typography variant='h6' gutterBottom>
              Patient Demographics
            </Typography>
            <Doughnut data={demographicsData} />
          </Paper>
        </Grid>

        {/* Common Diagnoses */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ padding: 3 }}>
            <Typography variant='h6' gutterBottom>
              Common Mental Health Diagnoses
            </Typography>
            <Bar data={commonDiagnosesData} />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PsychiatricReportsAnalytics;
